import React, { useState, useEffect } from 'react';
import { galleryImages } from '../mockData';

const Gallery = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentImageIndex, setCurrentImageIndex] = useState({});

  const categories = [
    { id: 'all', label: 'All' },
    { id: 'facility', label: 'Facilities' },
    { id: 'training', label: 'Training Sessions' }
  ];

  const filteredImages = selectedCategory === 'all' 
    ? galleryImages 
    : galleryImages.filter(img => img.category === selectedCategory);

  // Auto-rotate images for items with multiple images
  useEffect(() => {
    const intervals = {};
    
    filteredImages.forEach((image) => {
      if (image.images && image.images.length > 1) {
        intervals[image.id] = setInterval(() => {
          setCurrentImageIndex((prev) => ({
            ...prev,
            [image.id]: ((prev[image.id] || 0) + 1) % image.images.length
          }));
        }, 3000); // Change every 3 seconds
      }
    });

    return () => {
      Object.values(intervals).forEach(clearInterval);
    };
  }, [filteredImages]);

  const getImageUrl = (image) => {
    if (image.images && image.images.length > 0) {
      const index = currentImageIndex[image.id] || 0;
      return image.images[index];
    }
    return image.imageUrl;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1e3a8a] to-[#2563eb] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Our Gallery
            </h1>
            <p className="text-lg md:text-xl text-gray-100">
              Take a virtual tour of our state-of-the-art training facility and see our students in action
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-gray-50 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-2 rounded-full font-medium transition-all ${
                  selectedCategory === category.id
                    ? 'bg-[#1e3a8a] text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredImages.map((image) => {
              const displayImageUrl = getImageUrl(image);
              const hasMultipleImages = image.images && image.images.length > 1;
              
              return (
                <div
                  key={image.id}
                  className="group relative bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow"
                >
                  {/* Display actual image if available, otherwise show placeholder */}
                  {displayImageUrl ? (
                    <div className="aspect-video overflow-hidden bg-gray-100 relative">
                      <img 
                        key={displayImageUrl}
                        src={displayImageUrl} 
                        alt={image.title}
                        className="w-full h-full object-contain group-hover:scale-105 transition-all duration-1000 ease-in-out animate-fadeSlide"
                        style={{
                          animation: hasMultipleImages ? 'fadeSlide 1s ease-in-out' : 'none'
                        }}
                      />
                      {/* Slideshow indicator */}
                      {hasMultipleImages && (
                        <div className="absolute bottom-2 right-2 flex gap-1">
                          {image.images.map((_, idx) => (
                            <div
                              key={idx}
                              className={`rounded-full transition-all duration-500 ${
                                idx === (currentImageIndex[image.id] || 0)
                                  ? 'bg-[#00B7B2] w-4 h-2'
                                  : 'bg-white/50 w-2 h-2'
                              }`}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="aspect-video bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                      <div className="text-center p-6">
                        <div className="w-16 h-16 bg-[#1e3a8a]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                          <svg className="w-8 h-8 text-[#1e3a8a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <p className="text-sm text-gray-500 font-medium">{image.title}</p>
                      </div>
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {image.title}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {image.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Info Note */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg p-8 shadow-sm text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Experience Our Facility
            </h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Our training center features state-of-the-art dental equipment, simulation labs, and comfortable learning spaces designed to provide the best possible preparation environment.
            </p>
            <p className="text-sm text-gray-500">
              Images will showcase our modern facility, equipment, and training sessions
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Gallery;
